import React from "react";

/**
 * EyeShapeIcon — tiny SVG diagram of each eye shape.
 *
 * Six shapes: hooded, monolid, almond, round, downturned, upturned.
 * Drawn as a single illustrative eye (lid line + iris + lash hint) so
 * the user can match what they see in the mirror without us needing
 * a stock photo per shape.
 *
 * The geometry is intentionally minimal — same iris circle in every
 * shape, only the upper-lid curve + lash placement change. This keeps
 * the six shapes feeling like variants of the same eye rather than
 * six different illustrations.
 */

const COLORS = {
  ink: "#2C2123",
  iris: "#9B6A6C",
  pupil: "#1A1416",
  highlight: "#FAEFE9",
  brow: "#C9A8A4",
  fold: "#D5BBB6",
};

const VB = "0 0 100 60";

// Reusable iris + pupil + catchlight, centered at (cx, cy)
const Iris = ({ cx, cy, r = 9 }) => (
  <>
    <circle cx={cx} cy={cy} r={r} fill={COLORS.iris} />
    <circle cx={cx} cy={cy} r={r * 0.5} fill={COLORS.pupil} />
    <circle
      cx={cx - r * 0.3}
      cy={cy - r * 0.3}
      r={r * 0.18}
      fill={COLORS.highlight}
    />
  </>
);

// One <svg> per shape. Each draws:
//   - brow line at top
//   - upper-lid curve (the shape-distinguishing path)
//   - iris/pupil
//   - lower-lid curve
//   - optional "hood" or "monolid plane" hint
// Coordinates are picked so every eye reads at the same scale.
export const EyeShapeIcon = ({ shape, size = 60 }) => {
  const common = {
    width: size,
    height: size * 0.6,
    viewBox: VB,
    fill: "none",
    "aria-hidden": true,
  };

  switch (shape) {
    case "hooded":
      return (
        <svg {...common}>
          {/* brow */}
          <path d="M14 8 Q50 4 86 9" stroke={COLORS.brow} strokeWidth="2.2" strokeLinecap="round" />
          {/* hood fold — sits LOW over the lid */}
          <path d="M16 22 Q50 13 84 22" stroke={COLORS.fold} strokeWidth="1.4" strokeLinecap="round" strokeDasharray="1.5 2" />
          {/* upper lid (almost flat — hidden under the hood) */}
          <path d="M18 28 Q50 24 82 28" stroke={COLORS.ink} strokeWidth="2.2" strokeLinecap="round" />
          {/* lower lid */}
          <path d="M18 28 Q50 50 82 28" stroke={COLORS.ink} strokeWidth="2" strokeLinecap="round" />
          <Iris cx={50} cy={36} r={9} />
        </svg>
      );

    case "monolid":
      return (
        <svg {...common}>
          <path d="M14 8 Q50 4 86 9" stroke={COLORS.brow} strokeWidth="2.2" strokeLinecap="round" />
          {/* NO crease line - just a single continuous lid sweep */}
          <path d="M18 26 Q50 20 82 26" stroke={COLORS.ink} strokeWidth="2.2" strokeLinecap="round" />
          <path d="M18 26 Q50 48 82 26" stroke={COLORS.ink} strokeWidth="2" strokeLinecap="round" />
          {/* subtle horizontal shading to suggest the unbroken plane */}
          <path d="M22 18 Q50 14 78 18" stroke={COLORS.fold} strokeWidth="0.9" strokeLinecap="round" opacity="0.5" />
          <Iris cx={50} cy={34} r={9} />
        </svg>
      );

    case "almond":
      return (
        <svg {...common}>
          <path d="M14 8 Q50 4 86 9" stroke={COLORS.brow} strokeWidth="2.2" strokeLinecap="round" />
          {/* visible crease, gentle lift at outer corner */}
          <path d="M18 22 Q50 14 84 18" stroke={COLORS.fold} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="1.5 2" />
          <path d="M18 28 Q50 20 84 24" stroke={COLORS.ink} strokeWidth="2.2" strokeLinecap="round" />
          <path d="M18 28 Q50 46 84 24" stroke={COLORS.ink} strokeWidth="2" strokeLinecap="round" />
          <Iris cx={50} cy={32} r={9} />
        </svg>
      );

    case "round":
      return (
        <svg {...common}>
          <path d="M14 8 Q50 4 86 9" stroke={COLORS.brow} strokeWidth="2.2" strokeLinecap="round" />
          <path d="M22 22 Q50 14 78 22" stroke={COLORS.fold} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="1.5 2" />
          {/* wider vertical opening — closer to a circle */}
          <path d="M22 30 Q50 16 78 30" stroke={COLORS.ink} strokeWidth="2.2" strokeLinecap="round" />
          <path d="M22 30 Q50 52 78 30" stroke={COLORS.ink} strokeWidth="2" strokeLinecap="round" />
          <Iris cx={50} cy={34} r={10} />
        </svg>
      );

    case "downturned":
      return (
        <svg {...common}>
          <path d="M14 8 Q50 4 86 10" stroke={COLORS.brow} strokeWidth="2.2" strokeLinecap="round" />
          {/* outer corner LOWER than inner */}
          <path d="M18 22 Q50 16 84 24" stroke={COLORS.fold} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="1.5 2" />
          <path d="M18 26 Q50 22 84 32" stroke={COLORS.ink} strokeWidth="2.2" strokeLinecap="round" />
          <path d="M18 26 Q50 48 84 32" stroke={COLORS.ink} strokeWidth="2" strokeLinecap="round" />
          <Iris cx={48} cy={35} r={9} />
        </svg>
      );

    case "upturned":
      return (
        <svg {...common}>
          <path d="M14 10 Q50 4 86 8" stroke={COLORS.brow} strokeWidth="2.2" strokeLinecap="round" />
          {/* outer corner HIGHER than inner */}
          <path d="M18 28 Q50 16 84 18" stroke={COLORS.fold} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="1.5 2" />
          <path d="M18 32 Q50 22 84 22" stroke={COLORS.ink} strokeWidth="2.2" strokeLinecap="round" />
          <path d="M18 32 Q50 48 84 22" stroke={COLORS.ink} strokeWidth="2" strokeLinecap="round" />
          <Iris cx={52} cy={35} r={9} />
        </svg>
      );

    default:
      return null;
  }
};

export default EyeShapeIcon;
